import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, X as XIcon, User, Mail, Phone, CalendarDays, Clock, Scissors, Sparkles, Tag, FileText, Plus, Send, History, ChevronDown, ChevronUp } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";

const STATUS_OPTIONS = [
  { value: 'Confirmed', label: 'Confirmed', className: 'bg-green-100 text-green-700' },
  { value: 'Pending', label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'Completed', label: 'Completed', className: 'bg-blue-100 text-blue-700' },
  { value: 'Cancelled', label: 'Cancelled', className: 'bg-red-100 text-red-700' },
  { value: 'No-show', label: 'No-show', className: 'bg-gray-100 text-gray-700' },
];
const PAY_STATUS_OPTIONS = [
  { value: 'Unpaid', label: 'Unpaid' },
  { value: 'Deposit', label: 'Deposit paid' },
  { value: 'Paid', label: 'Fully paid' },
  { value: 'Complimentary', label: 'Complimentary' },
  { value: 'Refunded', label: 'Refunded' },
];
const PAY_TYPE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank', label: 'Bank' },
  { value: 'gift_card', label: 'Gift Card' },
];

const STATUS_BADGE = {
  Confirmed: 'bg-green-100 text-green-700 border-green-200',
  Pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Completed: 'bg-blue-100 text-blue-700 border-blue-200',
  Cancelled: 'bg-red-100 text-red-700 border-red-200',
  'No-show': 'bg-gray-100 text-gray-700 border-gray-200',
};
const PAY_BADGE = {
  Unpaid: 'bg-red-100 text-red-700 border-red-200',
  Deposit: 'bg-amber-100 text-amber-700 border-amber-200',
  Paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Complimentary: 'bg-purple-100 text-purple-700 border-purple-200',
  Refunded: 'bg-gray-100 text-gray-700 border-gray-200',
};

const COLOUR_EXTRA_REGEX = /colour|color|polish|shellac/i;

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <Icon size={14} className="text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

export default function AppointmentDetailsDialog({ appointment, open, onOpenChange, onSaved }) {
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [editForm, setEditForm] = useState({});
  const [resendPrompt, setResendPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const buildEditForm = (appt) => ({
    client_name: appt.client_name || '',
    client_email: appt.client_email || '',
    client_phone: appt.client_phone || '',
    appointment_datetime: appt.appointment_datetime ? appt.appointment_datetime.slice(0, 16) : '',
    services: (appt.services || []).map(s => ({
      ...s,
      selected_extras: s.selected_extras || [],
    })),
    total_price: appt.total_price || 0,
    discount: appt.discount || 0,
    notes: appt.notes || '',
    booking_status: appt.booking_status || 'Confirmed',
    payment_status: appt.payment_status || 'Unpaid',
    payment_type: appt.payment_type || 'cash',
    staff_name: appt.staff_name || '',
    verified_nail_length: appt.verified_nail_length || (appt.services || []).find(s => s.selected_length)?.selected_length || '',
    adjustment_note: '',
    adjustment_at: '',
  });

  useEffect(() => {
    if (open && appointment) {
      setMode('view');
      setEditForm(buildEditForm(appointment));
    }
  }, [open, appointment]);

  if (!appointment) return null;

  const a = appointment;
  const startDt = a.appointment_datetime;
  const dur = a.duration_minutes || 60;

  const recomputeTotal = (services, discount) => {
    const sub = services.reduce((s, svc) => s + (parseFloat(svc.price) || 0), 0);
    return Math.max(0, sub - (parseFloat(discount) || 0));
  };

  // Whether Total/Discount has actually changed from the saved appointment — used to
  // surface the optional backdate/note fields only when a correction is actually being made.
  const priceChanged = Math.abs((parseFloat(editForm.total_price) || 0) - (a.total_price || 0)) >= 0.01
    || Math.abs((parseFloat(editForm.discount) || 0) - (a.discount || 0)) >= 0.01;

  const updateService = (idx, field, value) => {
    setEditForm(prev => {
      const services = [...prev.services];
      services[idx] = { ...services[idx], [field]: value };
      return { ...prev, services, total_price: recomputeTotal(services, prev.discount) };
    });
  };

  // Service `price` is always the all-inclusive line total (base + length uplift + add-ons),
  // matching how bookings are priced at creation time. So whenever an add-on's price is
  // added/edited/removed here, we roll the exact delta into svc.price and recompute the
  // grand total live — otherwise add-ons silently never reach the appointment total.
  const updateExtra = (sIdx, eIdx, field, value) => {
    setEditForm(prev => {
      const services = [...prev.services];
      const extras = [...(services[sIdx].selected_extras || [])];
      const oldPrice = parseFloat(extras[eIdx]?.price) || 0;
      extras[eIdx] = { ...extras[eIdx], [field]: field === 'price' ? (parseFloat(value) || 0) : value };
      const newPrice = field === 'price' ? (parseFloat(extras[eIdx].price) || 0) : oldPrice;
      const delta = newPrice - oldPrice;
      services[sIdx] = { ...services[sIdx], selected_extras: extras, price: (parseFloat(services[sIdx].price) || 0) + delta };
      return { ...prev, services, total_price: recomputeTotal(services, prev.discount) };
    });
  };

  const addExtra = (sIdx) => {
    setEditForm(prev => {
      const services = [...prev.services];
      const extras = [...(services[sIdx].selected_extras || []), { name: '', price: 0 }];
      services[sIdx] = { ...services[sIdx], selected_extras: extras };
      // New add-on starts at £0, so no total change yet — it'll roll in once a price is typed.
      return { ...prev, services, total_price: recomputeTotal(services, prev.discount) };
    });
  };

  const removeExtra = (sIdx, eIdx) => {
    setEditForm(prev => {
      const services = [...prev.services];
      const removedPrice = parseFloat((services[sIdx].selected_extras || [])[eIdx]?.price) || 0;
      const extras = (services[sIdx].selected_extras || []).filter((_, j) => j !== eIdx);
      services[sIdx] = { ...services[sIdx], selected_extras: extras, price: Math.max(0, (parseFloat(services[sIdx].price) || 0) - removedPrice) };
      return { ...prev, services, total_price: recomputeTotal(services, prev.discount) };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        total_price: parseFloat(editForm.total_price) || 0,
        discount: parseFloat(editForm.discount) || 0,
      };
      const res = await axios.put(`${API}/appointments/${a.id}`, payload);
      toast.success('Appointment updated');
      const keyChanged = res.data?._key_fields_changed;
      setMode('view');
      // Pass the freshly-saved doc up so the parent can refresh detailsAppt immediately —
      // otherwise the view underneath the resend-confirmation prompt briefly shows stale totals.
      if (onSaved) onSaved(res.data);
      if (keyChanged) {
        setResendPrompt(true);
      } else {
        onOpenChange(false);
      }
    } catch (e) {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const resendConfirmation = async () => {
    try {
      const res = await axios.post(`${API}/appointments/${a.id}/resend-confirmation`);
      if (res.data.success) toast.success(`Updated confirmation sent to ${res.data.sent_to}`);
      else toast.error(res.data.error || 'Failed to send');
    } catch { toast.error('Failed to resend'); }
    setResendPrompt(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appt-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <span>{mode === 'view' ? 'Appointment Details' : 'Edit Appointment'}</span>
              {mode === 'view' && (
                <Button
                  variant="outline" size="sm"
                  className="rounded-xl text-xs gap-1.5"
                  data-testid="appt-details-edit-btn"
                  onClick={() => setMode('edit')}
                >
                  <Pencil size={12} /> Edit
                </Button>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              View or edit appointment details including client info, services, nail length, finishes and status.
            </DialogDescription>
          </DialogHeader>

          {/* ───────── VIEW MODE ───────── */}
          {mode === 'view' && (
            <div className="space-y-4">
              {/* Status pills */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`rounded-full border ${STATUS_BADGE[a.booking_status] || ''}`}>{a.booking_status}</Badge>
                <Badge className={`rounded-full border ${PAY_BADGE[a.payment_status] || ''}`}>{a.payment_status}</Badge>
                {a.voucher_code_applied && (
                  <Badge className="rounded-full bg-pink-100 text-pink-700 border border-pink-200">
                    Voucher: {a.voucher_code_applied} (−£{(a.voucher_amount_applied || 0).toFixed(2)})
                  </Badge>
                )}
                {a.promo_code_applied && (
                  <Badge className="rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                    Promo: {a.promo_code_applied} (−£{(a.promo_amount_applied || 0).toFixed(2)})
                  </Badge>
                )}
              </div>

              {/* Client */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-muted/30 rounded-xl">
                <Row icon={User} label="Client">
                  <p className="font-medium" data-testid="appt-detail-name">{a.client_name || '—'}</p>
                </Row>
                <Row icon={Phone} label="Phone">
                  {a.client_phone ? (
                    <a href={`tel:${a.client_phone}`} className="text-primary hover:underline" data-testid="appt-detail-phone">
                      {a.client_phone}
                    </a>
                  ) : <span className="text-muted-foreground">—</span>}
                </Row>
                <Row icon={Mail} label="Email">
                  {a.client_email ? (
                    <a href={`mailto:${a.client_email}`} className="text-primary hover:underline break-all text-xs" data-testid="appt-detail-email">
                      {a.client_email}
                    </a>
                  ) : <span className="text-muted-foreground">—</span>}
                </Row>
              </div>

              {/* Date + staff */}
              <div className="grid grid-cols-2 gap-2 p-3 bg-muted/30 rounded-xl">
                <Row icon={CalendarDays} label="Date & Time">
                  <p className="font-medium">{safeFormat(startDt, 'EEE d MMM yyyy, HH:mm')}</p>
                  <p className="text-xs text-muted-foreground">{dur} minutes</p>
                </Row>
                <Row icon={User} label="Staff">
                  <p className="font-medium">{a.staff_name || '—'}</p>
                </Row>
              </div>

              {/* Services */}
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
                  <Scissors size={12} /> Services
                </p>
                <div className="space-y-2">
                  {(a.services || []).map((svc, i) => (
                    <div key={i} className="p-3 border rounded-xl bg-white" data-testid={`appt-detail-service-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">{svc.name}</p>
                        <span className="text-sm font-semibold tabular-nums shrink-0">£{(svc.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock size={11} /> {svc.duration_minutes} min</span>
                        {svc.base_price !== undefined && svc.base_price !== svc.price && (
                          <span>Base £{(svc.base_price || 0).toFixed(2)}</span>
                        )}
                      </div>
                      {svc.selected_length && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Nail length:</span>
                          <Badge variant="outline" className="rounded-full text-xs">
                            {svc.selected_length}
                            {svc.length_uplift > 0 && ` (+£${(svc.length_uplift || 0).toFixed(2)})`}
                          </Badge>
                        </div>
                      )}
                      {(() => {
                        const colourExtra = (svc.selected_extras || []).find(ex => COLOUR_EXTRA_REGEX.test(ex.name || ''));
                        return colourExtra ? (
                          <div className="mt-2 flex items-center gap-1.5" data-testid={`appt-detail-colour-${i}`}>
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Colour:</span>
                            <Badge variant="outline" className="rounded-full text-xs bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700">
                              {colourExtra.name}
                              {colourExtra.price > 0 && ` (+£${(colourExtra.price || 0).toFixed(2)})`}
                            </Badge>
                          </div>
                        ) : null;
                      })()}
                      {(svc.selected_extras || []).filter(ex => !COLOUR_EXTRA_REGEX.test(ex.name || '')).length > 0 && (
                        <div className="mt-2">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
                            <Sparkles size={10} /> Finishes / Add-ons
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {svc.selected_extras.filter(ex => !COLOUR_EXTRA_REGEX.test(ex.name || '')).map((ex, j) => (
                              <Badge key={j} variant="outline" className="rounded-full text-xs bg-pink-50 border-pink-200 text-pink-700">
                                {ex.name} {ex.price > 0 && `+£${(ex.price || 0).toFixed(2)}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {(!a.services || a.services.length === 0) && (
                    <p className="text-sm text-muted-foreground italic">No services recorded</p>
                  )}
                </div>
                {a.verified_nail_length && (
                  <div className="mt-2 flex items-center gap-1.5" data-testid="appt-detail-verified-length">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Verified nail length (seen in person):</span>
                    <Badge variant="outline" className="rounded-full text-xs bg-emerald-50 border-emerald-200 text-emerald-700">
                      {a.verified_nail_length}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="p-3 bg-muted/30 rounded-xl space-y-1 text-sm">
                <div className="flex items-center justify-between text-muted-foreground text-xs" data-testid="appt-detail-subtotal">
                  <span>Subtotal ({(a.services || []).length} service{(a.services || []).length === 1 ? '' : 's'}, incl. length &amp; add-ons)</span>
                  <span className="tabular-nums">£{(a.price_subtotal ?? (a.services || []).reduce((s, svc) => s + (parseFloat(svc.price) || 0), 0)).toFixed(2)}</span>
                </div>
                {(a.discount || 0) > 0 && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="flex items-center gap-1"><Tag size={12} /> Discount</span>
                    <span className="tabular-nums">−£{(a.discount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between font-semibold pt-1 border-t border-border/50">
                  <span>Total</span>
                  <span className="tabular-nums" data-testid="appt-detail-total">£{(a.total_price || 0).toFixed(2)}</span>
                </div>
                {(a.voucher_amount_applied || 0) > 0 && (
                  <div className="flex items-center justify-between text-pink-600 text-xs">
                    <span className="flex items-center gap-1"><Tag size={12} /> Voucher / promo ({a.voucher_code_applied})</span>
                    <span className="tabular-nums">−£{(a.voucher_amount_applied || 0).toFixed(2)}</span>
                  </div>
                )}
                {(a.promo_amount_applied || 0) > 0 && (
                  <div className="flex items-center justify-between text-purple-600 text-xs">
                    <span className="flex items-center gap-1"><Tag size={12} /> Promo code ({a.promo_code_applied})</span>
                    <span className="tabular-nums">−£{(a.promo_amount_applied || 0).toFixed(2)}</span>
                  </div>
                )}
                {a.payment_status === 'Deposit' && a.deposit_amount > 0 && (
                  <>
                    <div className="flex items-center justify-between text-emerald-700 text-xs pt-1 border-t border-border/50">
                      <span>Deposit paid</span>
                      <span className="tabular-nums">£{(a.deposit_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-amber-700 text-xs font-medium">
                      <span>Balance due</span>
                      <span className="tabular-nums" data-testid="appt-detail-balance-due">
                        £{Math.max(0, (a.total_price || 0) - (a.deposit_amount || 0) - (a.voucher_amount_applied || 0) - (a.promo_amount_applied || 0)).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                {a.payment_type && (
                  <p className="text-[10px] text-muted-foreground pt-1">Payment method: {a.payment_type}</p>
                )}
              </div>

              {/* Price History (audit trail of Total/Discount corrections) */}
              {(a.price_adjustments || []).length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowHistory(v => !v)}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="price-history-toggle"
                  >
                    <History size={12} />
                    Price history ({a.price_adjustments.length})
                    {showHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                  {showHistory && (
                    <div className="mt-2 space-y-2" data-testid="price-history-list">
                      {[...a.price_adjustments].reverse().map((adj) => (
                        <div key={adj.id} className="p-2.5 border rounded-xl bg-white text-xs" data-testid={`price-history-entry-${adj.id}`}>
                          <div className="flex items-center justify-between font-medium">
                            <span>£{(adj.old_total || 0).toFixed(2)} → £{(adj.new_total || 0).toFixed(2)}</span>
                            <span className="text-muted-foreground">{adj.adjusted_at ? safeFormat(adj.adjusted_at, 'd MMM yyyy, HH:mm') : '—'}</span>
                          </div>
                          {adj.old_discount !== adj.new_discount && (
                            <p className="text-muted-foreground mt-0.5">Discount: £{(adj.old_discount || 0).toFixed(2)} → £{(adj.new_discount || 0).toFixed(2)}</p>
                          )}
                          {adj.note && <p className="mt-0.5 italic">"{adj.note}"</p>}
                          <p className="text-muted-foreground mt-1">By {adj.admin_email || 'Unknown admin'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {a.notes && (
                <Row icon={FileText} label="Notes">
                  <p className="text-sm whitespace-pre-wrap">{a.notes}</p>
                </Row>
              )}
            </div>
          )}

          {/* ───────── EDIT MODE ───────── */}
          {mode === 'edit' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Client Name</Label><Input data-testid="edit-client-name" value={editForm.client_name || ''} onChange={e => setEditForm(p => ({...p, client_name: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
                <div><Label className="text-xs">Email</Label><Input data-testid="edit-client-email" value={editForm.client_email || ''} onChange={e => setEditForm(p => ({...p, client_email: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
                <div><Label className="text-xs">Phone</Label><Input data-testid="edit-client-phone" value={editForm.client_phone || ''} onChange={e => setEditForm(p => ({...p, client_phone: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Date & Time</Label><Input data-testid="edit-datetime" type="datetime-local" value={editForm.appointment_datetime || ''} onChange={e => setEditForm(p => ({...p, appointment_datetime: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
                <div><Label className="text-xs">Staff</Label><Input data-testid="edit-staff" value={editForm.staff_name || ''} onChange={e => setEditForm(p => ({...p, staff_name: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" /></div>
              </div>
              <div>
                <Label className="text-xs">Verified nail length <span className="text-muted-foreground/70">(what you actually saw in person — used to catch mismatched selections next time)</span></Label>
                <Input data-testid="edit-verified-length" value={editForm.verified_nail_length || ''} onChange={e => setEditForm(p => ({...p, verified_nail_length: e.target.value}))} placeholder="e.g. Medium" className="mt-1 rounded-xl h-9 text-sm" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">Services</Label>
                  <Button variant="ghost" size="sm" onClick={() => setEditForm(p => ({...p, services: [...p.services, { service_id:'', name:'', duration_minutes:30, price:0, selected_length:'', length_uplift:0, selected_extras:[] }]}))} className="rounded-lg h-7 text-xs">
                    <Plus size={12} className="mr-1" /> Add Service
                  </Button>
                </div>
                {(editForm.services || []).map((svc, i) => (
                  <div key={i} className="p-2.5 bg-muted/30 rounded-xl mb-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={svc.name || ''} onChange={e => updateService(i, 'name', e.target.value)} className="rounded-lg h-8 text-sm flex-1" placeholder="Service" data-testid={`edit-svc-name-${i}`} />
                      <Input type="number" value={svc.duration_minutes || 0} onChange={e => updateService(i, 'duration_minutes', parseInt(e.target.value) || 0)} className="rounded-lg h-8 text-sm w-16" />
                      <span className="text-xs">min</span>
                      <span className="text-xs">£</span>
                      <Input type="number" step="0.01" value={svc.price || 0} onChange={e => updateService(i, 'price', parseFloat(e.target.value) || 0)} className="rounded-lg h-8 text-sm w-20" data-testid={`edit-svc-price-${i}`} />
                      <Button variant="ghost" size="icon" onClick={() => setEditForm(p => ({...p, services: p.services.filter((_,j)=>j!==i)}))} className="h-7 w-7 rounded-lg text-destructive"><XIcon size={12} /></Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground italic -mt-1">Price above already includes any add-ons below — it updates automatically as you edit them.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Nail length</Label>
                        <Input value={svc.selected_length || ''} onChange={e => updateService(i, 'selected_length', e.target.value)} className="rounded-lg h-7 text-xs mt-0.5" placeholder="e.g. Short" data-testid={`edit-svc-length-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Length uplift (£)</Label>
                        <Input type="number" step="0.01" value={svc.length_uplift || 0} onChange={e => updateService(i, 'length_uplift', parseFloat(e.target.value) || 0)} className="rounded-lg h-7 text-xs mt-0.5" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-[10px] text-muted-foreground flex items-center gap-1"><Sparkles size={10} /> Finishes / Add-ons</Label>
                        <Button variant="ghost" size="sm" onClick={() => addExtra(i)} className="h-6 text-[10px] gap-1 px-2"><Plus size={10} /> Add</Button>
                      </div>
                      {(svc.selected_extras || []).map((ex, j) => (
                        <div key={j} className="flex items-center gap-1.5 mb-1">
                          <Input value={ex.name || ''} onChange={e => updateExtra(i, j, 'name', e.target.value)} className="rounded-lg h-7 text-xs flex-1" placeholder="Name (e.g. French)" data-testid={`edit-extra-name-${i}-${j}`} />
                          <span className="text-[10px]">£</span>
                          <Input type="number" step="0.01" value={ex.price || 0} onChange={e => updateExtra(i, j, 'price', e.target.value)} className="rounded-lg h-7 text-xs w-16" data-testid={`edit-extra-price-${i}-${j}`} />
                          <Button variant="ghost" size="icon" onClick={() => removeExtra(i, j)} className="h-6 w-6 rounded-lg text-destructive"><XIcon size={10} /></Button>
                        </div>
                      ))}
                      {(svc.selected_extras || []).length === 0 && (
                        <p className="text-[10px] text-muted-foreground italic">No add-ons</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Discount (£) <span className="text-muted-foreground/70">e.g. 5 for first-time client</span></Label><Input type="number" step="0.01" value={editForm.discount || 0} onChange={e => setEditForm(p => ({...p, discount: parseFloat(e.target.value) || 0, total_price: recomputeTotal(p.services, e.target.value)}))} className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-discount" /></div>
                <div><Label className="text-xs">Total (£)</Label><Input type="number" step="0.01" value={editForm.total_price || 0} onChange={e => setEditForm(p => ({...p, total_price: parseFloat(e.target.value)||0}))} className="mt-1 rounded-xl h-9 text-sm font-semibold" data-testid="edit-total" /></div>
              </div>

              {priceChanged && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2" data-testid="price-adjustment-fields">
                  <p className="text-[10px] uppercase tracking-wide text-amber-700 font-medium">Price correction — logged to Price History</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Adjusted on <span className="text-muted-foreground/70">(optional, backdate if needed)</span></Label>
                      <Input type="datetime-local" value={editForm.adjustment_at || ''} onChange={e => setEditForm(p => ({...p, adjustment_at: e.target.value}))} className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-adjustment-at" />
                    </div>
                    <div>
                      <Label className="text-xs">Reason <span className="text-muted-foreground/70">(optional)</span></Label>
                      <Input value={editForm.adjustment_note || ''} onChange={e => setEditForm(p => ({...p, adjustment_note: e.target.value}))} placeholder="e.g. Added gel removal add-on" className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-adjustment-note" />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Status</Label>
                  <Select value={editForm.booking_status||'Confirmed'} onValueChange={v=>setEditForm(p=>({...p,booking_status:v}))}>
                    <SelectTrigger className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Payment</Label>
                  <Select value={editForm.payment_status||'Unpaid'} onValueChange={v=>setEditForm(p=>({...p,payment_status:v}))}>
                    <SelectTrigger className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-payment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_STATUS_OPTIONS.map(p=><SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Method</Label>
                  <Select value={editForm.payment_type||'cash'} onValueChange={v=>setEditForm(p=>({...p,payment_type:v}))}>
                    <SelectTrigger className="mt-1 rounded-xl h-9 text-sm" data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_TYPE_OPTIONS.map(p=><SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div><Label className="text-xs">Notes</Label><Textarea value={editForm.notes||''} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))} className="mt-1 rounded-xl text-sm" rows={2} data-testid="edit-notes" /></div>
            </div>
          )}

          <DialogFooter>
            {mode === 'view' ? (
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="appt-details-close-btn">Close</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => { setEditForm(buildEditForm(a)); setMode('view'); }} className="rounded-xl" data-testid="edit-cancel-btn">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="rounded-xl" data-testid="edit-save-btn">{saving ? 'Saving…' : 'Save Changes'}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend confirmation prompt */}
      <Dialog open={resendPrompt} onOpenChange={(o) => { if (!o) { setResendPrompt(false); onOpenChange(false); } }}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>Appointment Updated</DialogTitle>
            <DialogDescription className="sr-only">
              Confirm whether to resend the updated booking confirmation email to the client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Would you like to send an updated confirmation to the client?</p>
            <p className="text-xs text-muted-foreground">This will send a new email with the updated details and an updated calendar invite.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setResendPrompt(false); onOpenChange(false); }} className="rounded-xl">Skip</Button>
            <Button onClick={resendConfirmation} className="rounded-xl" data-testid="resend-confirmation-btn">
              <Send size={14} className="mr-1" /> Send Updated Confirmation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
